import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { EventRegisterDialog } from "@/components/EventRegisterDialog";
import { Calendar, MapPin, ImageIcon, UserPlus, ArrowRight } from "lucide-react";
import { isHtml, toPlain } from "@/components/RichText";

const FALLBACK = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const DEFAULT_HERO = "https://static.prod-images.emergentagent.com/jobs/69bc8a3c-ed6c-4b85-a9a2-ec966ad0ef18/images/81d37fc5868807fe595c4969ea7b26704912b5cf84f99c71c6da6f0dddee747b.jpeg";

function status(dateStr) {
  if (!dateStr) return "archiwum";
  return new Date(dateStr) >= new Date() ? "nadchodzące" : "archiwum";
}

export default function Szkolenia() {
  const { t } = useContent();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [filter, setFilter] = useState("wszystkie");
  const [regTr, setRegTr] = useState(null);

  const loadTrainings = () => api.get("/trainings").then((r) => setTrainings(r.data)).catch(() => {});
  useEffect(() => { loadTrainings(); }, []);

  const filtered = trainings.filter((tr) => filter === "wszystkie" || status(tr.event_date) === filter);

  return (
    <div>
      <PageHeader label="Kalendarz branżowy" title="Szkolenia, warsztaty i konferencje" subtitle={t("szkolenia_intro")} bgImage={t("szkolenia_hero_image") ? fileUrl(t("szkolenia_hero_image")) : DEFAULT_HERO} />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mb-10 flex flex-wrap gap-2">
          {["wszystkie", "nadchodzące", "archiwum"].map((f) => (
            <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} data-testid={`szkolenia-filter-${f}`} className={filter === f ? "bg-ztk-blue capitalize" : "capitalize"}>{f}</Button>
          ))}
        </div>

        {filtered.length === 0 && <p className="text-muted-foreground">Brak wydarzeń w tej kategorii.</p>}

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tr) => {
            const st = status(tr.event_date);
            const cover = tr.images?.length ? fileUrl(tr.images[0]) : FALLBACK;
            const upcoming = st === "nadchodzące";
            const cap = tr.capacity || 0;
            const remaining = cap > 0 ? Math.max(0, cap - (tr.signup_count || 0)) : null;
            return (
              <div
                key={tr.id}
                data-testid={`event-card-${tr.id}`}
                onClick={() => navigate(`/szkolenia/${tr.id}`)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-48 overflow-hidden bg-ztk-navy">
                  <img src={cover} alt={tr.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${upcoming ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-200"}`}>{st}</span>
                  {tr.images?.length > 1 && (
                    <span className="absolute right-3 bottom-3 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-semibold text-white">
                      <ImageIcon className="h-3 w-3" /> {tr.images.length}
                    </span>
                  )}
                  {upcoming && cap > 0 && remaining !== null && (
                    <span className="absolute right-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ztk-navy" data-testid={`event-spots-${tr.id}`}>
                      {remaining} wolnych miejsc
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{tr.event_date ? new Date(tr.event_date).toLocaleDateString("pl-PL") : "—"}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{tr.location}</span>
                  </div>
                  {tr.kind === "konferencja" && <span className="inline-block rounded-full bg-ztk-yellow px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ztk-navydark" data-testid={`event-kind-${tr.id}`}>Konferencja</span>}
                  <h3 className="mt-3 font-display text-lg font-semibold text-ztk-navy dark:text-white">{tr.title}</h3>
                  {(() => {
                    const pk = tr.packages || [];
                    const label = pk.length ? (pk.length > 1 ? `od ${pk[0].price}` : pk[0].price) : tr.price;
                    return label ? <p className="mt-1 text-sm font-bold text-ztk-blue" data-testid={`event-price-${tr.id}`}>{label}</p> : null;
                  })()}
                  <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted-foreground">{isHtml(tr.description) ? toPlain(tr.description) : tr.description}</p>
                  <div className="mt-4 flex items-center gap-2">
                    {upcoming && (
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); setRegTr(tr); }} data-testid={`event-signup-${tr.id}`} className="gap-1.5 bg-ztk-blue hover:bg-ztk-navy800">
                        <UserPlus className="h-4 w-4" /> Weź udział
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="ml-auto gap-1.5 px-2 text-ztk-blue" onClick={(e) => { e.stopPropagation(); navigate(`/szkolenia/${tr.id}`); }} data-testid={`event-details-${tr.id}`}>
                      Szczegóły <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <EventRegisterDialog training={regTr} onClose={() => setRegTr(null)} />
    </div>
  );
}
